import { useState, useEffect, useCallback } from 'react';

/**
 * E1 — small shared data-fetching hook for the dashboard foundation pages.
 * Real loading/error/empty states throughout (no page pretends to have
 * data it hasn't actually fetched).
 */
export function useApiData(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load this data. Please try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

export function StateBlock({ loading, error, isEmpty, emptyLabel, onRetry, children }) {
  if (loading) return <p className="text-white/50">Loading…</p>;
  if (error) {
    return (
      <div className="text-red-400 text-sm">
        {error}{' '}
        {onRetry && (
          <button onClick={onRetry} className="underline hover:text-red-300 ml-1">
            Retry
          </button>
        )}
      </div>
    );
  }
  if (isEmpty) return <p className="text-white/40">{emptyLabel}</p>;
  return children;
}
